import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SlotTrendPoint } from "@clustec/common";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useNetworkStore } from "../stores/network";
import {
  useNetworkStats,
  useClusterRuns,
  useClusterDetail,
  useUmapData,
  useFeePayerStats,
  useChainHealth,
  useProductionStats,
} from "../api/hooks";
import { ScatterPlot3D } from "../components/ScatterPlot3D";
import { PageContainer, PageTitle, Card, StatValue, StatLabel, Loading } from "../components/ui";
import { useLabelResolver } from "../hooks/useAddressResolver";
import { theme } from "../lib/theme";
import { noAnimation } from "../lib/chart";
import { media } from "../lib/breakpoints";
import styled from "@emotion/styled";
import { abbreviateHex } from "../components/TxTable";
import { Sparkline } from "../components/Sparkline";
import { EpochTimeline } from "../components/EpochTimeline";
import { EpochMissStrip } from "../components/EpochMissStrip";
import { slotStatusColor } from "../components/SlotStatusLabel";
// Subpath, not the package root: the root barrel re-exports the drizzle schema and would pull
// `postgres` into the browser bundle.
import { deriveMissCauses } from "@clustec/common/slot-status";
import { FinalityPipeline } from "../components/FinalityPipeline";
import { Link } from "react-router-dom";

/** Mean of a series that has gaps; null slots are absent readings, not zeroes. */
export function avgOf(series: (number | null)[]): number {
  const vals = series.filter((v): v is number => v != null);
  return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
}

/** Spread of the same gappy series, for the cadence jitter shown beside the mean block time. */
export function stdDevOf(series: (number | null)[]): number {
  const vals = series.filter((v): v is number => v != null);
  if (vals.length < 2) return 0;
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  return Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length);
}

/**
 * One ratio per slot, for a line whose shape means what its headline says.
 *
 * Every sparkline the health card receives is per slot, and a slot carries a whole checkpoint of one
 * or more L2 blocks that all share its timestamp: `blockTimes` is the gap between slots, while
 * `txCounts` and `manaUsed` are sums across the slot's blocks. So none of them is a per-block or
 * per-second figure on its own — each needs dividing by the right denominator, which is what this
 * does:
 *
 *   tx / s        = txs in the slot  ÷ the interval it closed
 *   block time    = the interval     ÷ blocks the slot produced
 *   mana / block  = mana in the slot ÷ blocks the slot produced
 *
 * Null wherever either side is missing, which is the honest answer twice over: the first slot of a
 * window has no preceding block to measure an interval against, and a missed slot has neither.
 */
export function ratioSeries(
  numerators: (number | null)[],
  denominators: (number | null)[] | undefined,
): (number | null)[] {
  if (denominators == null) return [];
  return numerators.map((n, i) => {
    const d = denominators[i];
    return n != null && d != null && d > 0 ? n / d : null;
  });
}

/**
 * The window's own ratio: the totals divided, never the mean of the per-slot ratios.
 *
 * A mean of ratios weights every slot equally, so one quiet 216s gap counts for as much as a busy
 * 12s one and the answer is a figure the chain never ran at. Summing first is also what makes block
 * time agree with `production.blocks.avgIntervalSeconds`, which that API defines as elapsed time
 * over block count and documents as *not* the slot duration and *not* the checkpoint cadence.
 */
export function ratioOf(
  numerators: (number | null)[],
  denominators: (number | null)[] | undefined,
): number | null {
  if (denominators == null) return null;
  let top = 0;
  let bottom = 0;
  for (let i = 0; i < denominators.length; i++) {
    const n = numerators[i];
    const d = denominators[i];
    // Both sides, or neither. Counting a slot whose interval is unmeasured — the window's first,
    // or one following a gap — would add its blocks to the denominator without adding the time
    // that produced them, and report a block time faster than the chain ran.
    if (n == null || d == null || d <= 0) continue;
    bottom += d;
    top += n;
  }
  return bottom > 0 ? top / bottom : null;
}

/**
 * Two decimals is right for a busy chain and useless for a quiet one: a testnet running a few
 * transactions a minute is genuinely 0.004 tx/s, and rounding that to "0.00" reports a stopped
 * chain. Below a hundredth, switch to two significant figures instead.
 */
export function formatRate(v: number): string {
  if (v === 0 || v >= 0.01) return v.toFixed(2);
  return Number(v.toPrecision(2)).toString();
}

/**
 * Same thresholds as the Slot Utilization health indicator (green above 85% utilisation, yellow
 * above 50%), expressed as a miss rate. Reusing them stops the strip from shouting while the
 * indicator two rows up says everything is fine.
 */
export function missTone(missedPct: number): "ok" | "warn" | "bad" {
  if (missedPct >= 50) return "bad";
  if (missedPct >= 15) return "warn";
  return "ok";
}

/**
 * Tier 2's horizon. 24 epochs is ~15h at 72s slots — long enough to show a developing problem,
 * short enough to still be reacting to today. One constant.
 */
const RECENT_EPOCHS = 24;

function formatMana(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return Math.round(value).toLocaleString();
}

// ── Styled ──

/**
 * One screenful. `flex: 1` inside the layout's grid takes the space left under the search bar;
 * `100vh` took the whole viewport and pushed the bar's height out the bottom.
 *
 * `auto` rather than `hidden`, so the one case that cannot fit — a window too short for the health
 * card's content plus a usable plot — scrolls rather than silently cutting the plot off.
 */
const DashboardContainer = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  flex: 1;
  min-height: 0;
  overflow: auto;

  /* On a phone the page scrolls instead, so this grows with its content and stays out of the way of
     the scroll container above it — a nested scroll here would swallow the page's own. */
  ${media.md} {
    overflow: visible;
  }
`;

const TopRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: stretch;
  flex-shrink: 0;

  ${media.md} {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

/**
 * Ten stacked sections, so the gap between them is the single biggest term in this card's height —
 * at 16px it spent 144px on air and took 69% of the viewport, leaving the cluster map a sliver.
 * 8px still separates the tiers legibly, and the dividers do the rest of that work anyway.
 */
const HealthCard = styled(Card)`
  flex: 3;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};

  ${media.md} {
    padding: ${theme.spacing.sm};
  }
`;

const HealthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
`;

const OverallBadge = styled.span<{ health: "healthy" | "degraded" | "unhealthy" }>`
  font-size: ${theme.fontSize.xs};
  font-weight: 600;
  padding: 2px 8px;
  border-radius: ${theme.radius.sm};
  background: ${(p) =>
    p.health === "healthy"
      ? `${theme.colors.success}22`
      : p.health === "degraded"
        ? `${theme.colors.warning}22`
        : `${theme.colors.danger}22`};
  color: ${(p) =>
    p.health === "healthy"
      ? theme.colors.success
      : p.health === "degraded"
        ? theme.colors.warning
        : theme.colors.danger};
`;

/**
 * Epoch selector for the card's top tier.
 *
 * It was dropped when Health gained a window selector, on the reasoning that the Dashboard is the
 * glance and historical browsing belongs on /health. That left the card unable to answer the
 * question it is best placed to answer — "was the epoch before this one like this too?" — and
 * pushed a one-click comparison onto a page load. Health still owns investigation: this steps the
 * top tier and nothing else, and every deep link out of the card still lands there.
 */
const EpochNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  color: ${theme.colors.text};
  cursor: pointer;
  font-size: ${theme.fontSize.sm};
  line-height: 1;
  padding: 1px 6px;
  &:disabled {
    color: ${theme.colors.textMuted};
    cursor: default;
  }
  &:not(:disabled):hover {
    background: ${theme.colors.bgHover};
  }
`;

/** Only rendered off-live, where it is the one-click way back to a card that tracks the head. */
const LatestButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  padding: 0 2px;
  &:hover {
    text-decoration: underline;
  }
`;

const EpochLabel = styled.span`
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  color: ${theme.colors.text};
  min-width: 80px;
  text-align: center;
`;

/** Server verdict on one metric, moved onto the sparkline cards along with the numbers. */
const StatusDot = styled.span<{ status: "green" | "yellow" | "red" }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) =>
    p.status === "green"
      ? theme.colors.success
      : p.status === "yellow"
        ? theme.colors.warning
        : theme.colors.danger};
`;

/**
 * Tier heading. The card carries exactly two temporal frames and each gets one label — the whole
 * readability problem was four elements on three axes, each captioning its own window in its own
 * unit, adjacent to elements on other windows. Nobody reconciles four captions; they read the
 * shapes and assume a shared axis.
 */
const TierLabel = styled.div`
  font-size: ${theme.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing.xs};
`;

/**
 * Tier 2 — a longer horizon than the rest of the card. Deliberately plain numbers rather than a
 * chart or a bar: text cannot be misread as a visualisation sharing the epoch axis above it, which
 * is precisely how the previous 24h bar came to look like an alarm about the current epoch.
 */
const RecentRow = styled.div`
  padding-top: ${theme.spacing.sm};
`;

const RecentHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
  flex-wrap: wrap;
`;

const RecentFooter = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xs};
  flex-wrap: wrap;
`;

/** Cause split as labelled dots rather than a bar — a bar here would read as a magnitude. */
const CauseDots = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const CauseDotItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: monospace;
  white-space: nowrap;
`;

const Dot = styled.span<{ color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 2px;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

/** The single most actionable thing in the window: which epoch to go and look at. */
const WorstLink = styled(Link)`
  margin-left: auto;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  color: ${theme.colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const TierAside = styled.span`
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  font-family: monospace;
`;

const RecentLabel = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const RecentValue = styled.span<{ tone: "ok" | "warn" | "bad" }>`
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  font-family: monospace;
  color: ${(p) =>
    p.tone === "bad"
      ? theme.colors.danger
      : p.tone === "warn"
        ? theme.colors.warning
        : theme.colors.text};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.border};
  margin: 0;

  ${media.sm} {
    display: none;
  }
`;

/**
 * The card's only carrier of block time, throughput and mana now that the indicator grid is gone,
 * so it is no longer hidden on a phone — those figures were the whole point of the tier.
 *
 * Fixed height rather than `flex: 1`: as a growing child it absorbed every bit of slack in the card,
 * which on a network with a long fee-payer legend beside it opened a band of empty space between the
 * lines and the tier below.
 */
const SparklineRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
  flex: 0 0 auto;

  ${media.sm} {
    gap: ${theme.spacing.sm};
  }
`;

const SparklineCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** Stacks below the sm breakpoint, where three labels and three values will not fit side by side. */
const SparklineHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.xs};

  ${media.sm} {
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
  }
`;

const SparklineValue = styled.span`
  font-size: ${theme.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.text};
  font-family: monospace;
  white-space: nowrap;
`;

/** The spread beside a mean, or the unit beside a rate — present but never competing with it. */
const SparklineAside = styled.span`
  font-weight: 400;
  color: ${theme.colors.textMuted};
`;

const SparklineLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

const SideColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  min-width: 0;

  ${media.md} {
    width: 100%;
    gap: ${theme.spacing.sm};
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};

  ${media.md} {
    gap: ${theme.spacing.sm};
  }
`;

const MiniStat = styled(Card)`
  flex: 1;
  text-align: center;
  padding: ${theme.spacing.sm};
`;

/**
 * `min-height: 0` so the legend cannot dictate the row's height. A flex item's automatic minimum is
 * its content, so on a network with many significant fee payers the list pushed this card — and with
 * it the whole top row, including the health card beside it — taller than the screen had room for.
 * The list scrolls instead.
 */
const PieCard = styled(Card)`
  padding: ${theme.spacing.md};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.md};
  flex: 1;
  min-height: 0;

  ${media.md} {
    padding: ${theme.spacing.sm};
    gap: ${theme.spacing.sm};
  }
`;

/** Takes the width the donut leaves, and the card's full height, so nothing reads as empty. */
const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
`;

/**
 * The box the fee-payer list scrolls inside, and the reason the list cannot set the row's height.
 *
 * Fee-payer counts on an evenly-distributed network leave many entries above the significance
 * threshold — mainnet lists nineteen — and a flex item's height contribution is its content, so in
 * normal flow that list decided how tall the top row was. `min-height: 0` does not help: it is a
 * floor, not a ceiling. The rows are therefore taken out of flow, which is what actually stops them
 * contributing, and this box gives them something definite to fill.
 */
const LegendViewport = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;

  /* On a phone the column has no definite height to fill, so out-of-flow rows would collapse to the
     donut's height. There the page scrolls anyway: back to normal flow with a capped, scrolling
     list, which is what this was before. */
  ${media.md} {
    position: static;
    max-height: 240px;
    overflow: hidden;
  }
`;

const LegendRows = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;

  ${media.md} {
    position: static;
    max-height: 240px;
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.fontSize.sm};
  line-height: 20px;
  white-space: nowrap;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  color: ${theme.colors.textMuted};
  font-family: monospace;
  font-size: ${theme.fontSize.xs};
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegendCount = styled.span`
  color: ${theme.colors.text};
  margin-left: ${theme.spacing.sm};
`;

/**
 * Square, on a fixed share of the card's width — a follower, not a driver.
 *
 * It was a fixed 220px, which left a quarter of the card empty. Letting it grow into the leftover
 * width instead overcorrected: it took 288px, dominated the card and squeezed the legend into a
 * column barely wide enough for an address. So the width is a share and the aspect ratio derives the
 * height, with `max-height` keeping it inside a short card. The vertical space the donut does not
 * need is taken up by the legend beside it, which is why the card no longer reads as half empty.
 */
const PieChartBox = styled.div`
  flex: 0 1 46%;
  min-width: 0;
  align-self: center;
  aspect-ratio: 1;
  max-height: 100%;

  /* A share here too, rather than the 90px it used to be pinned to: at that size on a phone it read
     as a stray icon sitting to the left of the legend instead of half of the card. Slightly smaller
     a share than on desktop, because an abbreviated address needs about the same absolute width
     whatever the screen. */
  ${media.md} {
    flex: 0 1 40%;
  }
`;

/**
 * Expanded, the plot leaves the page flow and takes the whole viewport. The dashboard's own layout
 * can't give it much more than it already has — the health card is the tall thing above it and
 * shrinking that would cost more than the plot gains — so "more real estate" means all of it.
 */
const PlotSection = styled.div<{ $expanded: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${(p) =>
    p.$expanded &&
    `
      position: fixed;
      inset: 0;
      /* Over the sticky mobile header (50), under the nav drawer, tx clipboard and search
         (90–300): a maximised chart should not outrank the app's own chrome. */
      z-index: 80;
      padding: ${theme.spacing.sm};
      background: ${theme.colors.bg};
    `}
`;

/**
 * Takes the height left over once the health card has had what its content needs.
 *
 * The floor is a fallback, not a request: a 400px minimum asked for more than a laptop page had and
 * overflowed by the difference, but with no floor at all a short window squeezed the plot to a few
 * pixels and clipped it. At 180px the plot stays usable and the dashboard scrolls instead — a
 * reachable plot beats a hidden one, and on any normal screen the leftover is far more than this.
 */
const PlotCard = styled(Card)`
  padding: ${theme.spacing.sm};
  overflow: hidden;
  flex: 1;
  min-height: 180px;

  ${media.md} {
    min-height: 400px;
  }
`;

const PlotHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

const PlotCaption = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};

  ${media.md} {
    display: none;
  }
`;

/** Stays visible on a phone, where the caption does not: it is the control, not the explanation. */
const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  margin-left: auto;
  padding: 4px 8px;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  line-height: 1;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.bgHover};
  }
`;

const MobileTitle = styled(PageTitle)`
  margin-bottom: 0;
  padding: 0;
  ${media.md} {
    font-size: ${theme.fontSize.lg};
  }
`;

const ICON_PROPS = {
  width: 13,
  height: 13,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function ExpandIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M14 10h6M20 10V4M10 14H4M4 14v6M20 4l-6 6M4 20l6-6" />
    </svg>
  );
}

// ── Component ──

export function Dashboard() {
  const { selectedNetwork } = useNetworkStore();
  const navigate = useNavigate();
  const resolveLabel = useLabelResolver();

  // Escape closes it, as it would any full-screen view. Bound only while expanded so it never
  // shadows a key the rest of the dashboard might want.
  const [plotExpanded, setPlotExpanded] = useState(false);
  useEffect(() => {
    if (!plotExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPlotExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [plotExpanded]);

  const { data: stats, isLoading } = useNetworkStats(selectedNetwork);
  // The live query, always: it supplies the head epoch the selector steps back from, so it is
  // needed even while a historical epoch is on screen.
  const { data: health } = useChainHealth(selectedNetwork);
  const currentEpoch = health?.currentEpochNumber ?? null;
  const slotsPerEpoch = health?.epochTimeline?.slotsPerEpoch ?? 32;

  // null follows the head rather than pinning whatever was current at mount, so a card left open
  // keeps refreshing. Stepping forward onto the head returns to null for the same reason — an
  // explicit epoch is cached indefinitely and would quietly stop updating.
  const [selectedEpoch, setSelectedEpoch] = useState<number | null>(null);
  const isLiveEpoch = selectedEpoch == null;
  const viewEpoch = selectedEpoch ?? currentEpoch;
  // Same query key as the live one when nothing is selected, so following the head costs no
  // second request.
  const { data: displayHealth } = useChainHealth(selectedNetwork, selectedEpoch ?? undefined);

  const stepEpoch = (delta: number) => {
    if (viewEpoch == null) return;
    const next = viewEpoch + delta;
    if (next < 0) return;
    setSelectedEpoch(currentEpoch != null && next >= currentEpoch ? null : next);
  };

  // The long tier ends where the selection does, so stepping back moves one window rather than
  // leaving a 24-epoch strip about the head under a timeline about an older epoch.
  const recentFromEpoch = viewEpoch != null ? Math.max(0, viewEpoch - RECENT_EPOCHS + 1) : null;
  // Requested as an exact epoch range rather than trailing hours, so the label on the row is
  // literally what was queried instead of an hours figure re-described in epochs.
  const recentFrom = recentFromEpoch != null ? recentFromEpoch * slotsPerEpoch : undefined;
  const recentTo = viewEpoch != null ? (viewEpoch + 1) * slotsPerEpoch - 1 : undefined;
  const { data: recentSummary } = useProductionStats(selectedNetwork, {
    from: recentFrom,
    to: recentTo,
    include: ["trend"],
    groupBy: "epoch",
    enabled: viewEpoch != null,
    // The range is explicit but its end is the head, so it keeps moving while the
    // newest epoch fills. Stepping back to a fixed epoch pins it again.
    live: isLiveEpoch,
  });
  const recentTone =
    recentSummary != null ? { tone: missTone(recentSummary.totals.missedPct) } : null;
  // Derived from the per-status counts rather than requested: the endpoint no longer ships a
  // miss-only array, and this card's query stays trend-only instead of pulling the whole
  // attestation profile for what is arithmetic over counts it already has.
  const recentCauses = deriveMissCauses(recentSummary?.statusCounts);
  // The epoch worth opening first. Only offered when something actually missed, so it never
  // points at a clean epoch as if it were a problem.
  const worstEpoch = (recentSummary?.trend?.points ?? []).reduce<SlotTrendPoint | null>(
    (worst, p) => (p.missed > 0 && (worst == null || p.missedPct > worst.missedPct) ? p : worst),
    null,
  );
  const sparkEpochs =
    displayHealth?.sparklines != null
      ? Math.max(
          1,
          Math.round(
            (displayHealth.sparklines.toSlot - displayHealth.sparklines.fromSlot + 1) /
              slotsPerEpoch,
          ),
        )
      : 2;

  // Headline figures for the sparkline tier, each derived from the very arrays its line draws.
  //
  // Every series arrives per slot, and a slot holds a whole checkpoint — mainnet averages 3.3 blocks
  // per checkpoint, all stamped with the slot's time. So each metric is divided into a per-block or
  // per-second figure before it is shown: read raw, `blockTimes` is the checkpoint cadence and
  // reports a block time pinned to the slot duration (72.0s on every network, whatever the load),
  // and `manaUsed` is mana per slot shown under a per-block label, overstated by the same 3.3×.
  //
  // The response also carries scalar `avgBlockTime` and `tps`, correctly block-level, but scoped to
  // the selected epoch while the series are a rolling two-epoch window. Deriving both the number and
  // the line from one window keeps this tier saying one thing.
  const spark = displayHealth?.sparklines;
  // Typed as always present, and the server always sends it — but the socket pushes health payloads
  // straight from whichever server process is running, and one that predates this field would
  // otherwise have the page divide by undefined and throw during a rolling restart. Absent, the
  // per-block figures simply are not derivable, so the card falls back rather than guessing.
  const blockCounts: (number | null)[] | undefined = spark?.blockCounts;
  const tpsPerSlot = useMemo(
    () => (spark ? ratioSeries(spark.txCounts, spark.blockTimes) : []),
    [spark],
  );
  const blockTimePerSlot = useMemo(
    () => (spark ? ratioSeries(spark.blockTimes, blockCounts) : []),
    [spark, blockCounts],
  );
  const manaPerBlock = useMemo(
    () => (spark ? ratioSeries(spark.manaUsed, blockCounts) : []),
    [spark, blockCounts],
  );
  const windowRate = spark ? ratioOf(spark.txCounts, spark.blockTimes) : null;
  // The scalar fallback is the same metric on a narrower window: block-level, epoch-scoped.
  const windowBlockTime =
    (spark ? ratioOf(spark.blockTimes, blockCounts) : null) ?? displayHealth?.avgBlockTime ?? null;
  const windowMana = spark ? ratioOf(spark.manaUsed, blockCounts) : null;
  const windowJitter = stdDevOf(blockTimePerSlot);

  // Thresholds stay server-side, so the dots keep reading from the indicators the grid used to
  // render. A metric the server stops reporting loses its dot rather than gaining a guessed colour.
  const statusOf = (metric: string) =>
    displayHealth?.indicators.find((i) => i.metric === metric)?.status ?? null;
  const blockTimeStatus = statusOf("Block Time");
  const throughputStatus = statusOf("Throughput");
  const manaStatus = statusOf("Avg Mana");

  const { data: runs } = useClusterRuns(selectedNetwork);
  const latestRun = runs?.[0];
  const runId = latestRun?.id ?? 0;
  const { data: detail } = useClusterDetail(selectedNetwork, runId);
  const { data: umapData, isLoading: umapLoading } = useUmapData(selectedNetwork, runId);
  const { data: feePayerData } = useFeePayerStats(selectedNetwork);

  const pieData = useMemo(() => {
    if (!feePayerData?.feePayers?.length) return [];
    const total = feePayerData.feePayers.reduce((s, f) => s + f.count, 0);
    const threshold = total * 0.02;
    const significant: { name: string; value: number; address: string }[] = [];
    let otherCount = 0;
    for (const fp of feePayerData.feePayers) {
      if (fp.count >= threshold) {
        const label = resolveLabel(fp.address);
        const name = label ?? abbreviateHex(fp.address);
        significant.push({ name, value: fp.count, address: fp.address });
      } else {
        otherCount += fp.count;
      }
    }
    if (otherCount > 0) {
      significant.push({ name: "Other", value: otherCount, address: "" });
    }
    return significant;
  }, [feePayerData, resolveLabel]);

  if (isLoading) return <Loading />;

  return (
    <DashboardContainer>
      <MobileTitle>Dashboard</MobileTitle>

      <TopRow>
        <HealthCard>
          <HealthHeader>
            <StatLabel style={{ fontWeight: 600, fontSize: theme.fontSize.sm }}>
              Chain Health
            </StatLabel>
            <EpochNav>
              <NavButton
                onClick={() => stepEpoch(-1)}
                disabled={viewEpoch == null || viewEpoch <= 0}
                title="Previous epoch"
              >
                ‹
              </NavButton>
              <EpochLabel>
                {displayHealth?.epochTimeline != null
                  ? `Epoch #${displayHealth.epochTimeline.epochNumber}`
                  : "—"}
                {isLiveEpoch && <span style={{ color: theme.colors.textMuted }}> (current)</span>}
              </EpochLabel>
              <NavButton onClick={() => stepEpoch(1)} disabled={isLiveEpoch} title="Next epoch">
                ›
              </NavButton>
              {!isLiveEpoch && (
                <LatestButton onClick={() => setSelectedEpoch(null)} title="Back to the live epoch">
                  latest
                </LatestButton>
              )}
            </EpochNav>
            {displayHealth && (
              <OverallBadge health={displayHealth.overallHealth}>{displayHealth.overallHealth}</OverallBadge>
            )}
          </HealthHeader>

          {displayHealth && (
            <>
              <TierLabel>
                {isLiveEpoch ? "This epoch" : `Epoch ${viewEpoch}`} ·{" "}
                {displayHealth.epochTimeline?.slotsPerEpoch ?? 32} slots
              </TierLabel>
              <EpochTimeline
                epochTimeline={displayHealth.epochTimeline}
                genesisTime={displayHealth.genesisTime}
                slotDuration={displayHealth.slotDuration}
                isLive={isLiveEpoch}
              />

              <FinalityPipeline
                proposedBlock={displayHealth.proposedBlock}
                proposedCheckpoint={displayHealth.proposedCheckpoint}
                proposedEpoch={displayHealth.proposedEpoch}
                checkpointedBlock={displayHealth.checkpointedBlock}
                checkpointedCheckpoint={displayHealth.checkpointedCheckpoint}
                checkpointedEpoch={displayHealth.checkpointedEpoch}
                provenBlock={displayHealth.provenBlock}
                provenCheckpoint={displayHealth.provenCheckpoint}
                provenEpoch={displayHealth.provenEpoch}
                finalizedBlock={displayHealth.finalizedBlock}
                finalizedCheckpoint={displayHealth.finalizedCheckpoint}
                finalizedEpoch={displayHealth.finalizedEpoch}
                proofWindow={displayHealth.proofWindow}
                genesisTime={displayHealth.genesisTime}
                slotDuration={displayHealth.slotDuration}
              />

              <Divider />

              <TierLabel>
                Last {sparkEpochs} epochs · {isLiveEpoch ? "rolling" : `to epoch ${viewEpoch}`}
                {displayHealth.sparklines != null && (
                  <TierAside>
                    {" "}
                    slots {displayHealth.sparklines.fromSlot}–{displayHealth.sparklines.toSlot}
                  </TierAside>
                )}
              </TierLabel>

              <SparklineRow>
                <SparklineCard>
                  <SparklineHeader>
                    <SparklineLabel>
                      {blockTimeStatus && <StatusDot status={blockTimeStatus} />}
                      Block time
                    </SparklineLabel>
                    <SparklineValue>
                      {windowBlockTime != null ? windowBlockTime.toFixed(1) : "—"}s{" "}
                      <SparklineAside>±{windowJitter.toFixed(1)}</SparklineAside>
                    </SparklineValue>
                  </SparklineHeader>
                  <Sparkline
                    data={blockTimePerSlot}
                    color={theme.colors.primary}
                    height={56}
                  />
                </SparklineCard>
                <SparklineCard>
                  <SparklineHeader>
                    <SparklineLabel>
                      {throughputStatus && <StatusDot status={throughputStatus} />}
                      Throughput
                    </SparklineLabel>
                    <SparklineValue>
                      {windowRate != null ? formatRate(windowRate) : "—"}{" "}
                      <SparklineAside>tx/s</SparklineAside>
                    </SparklineValue>
                  </SparklineHeader>
                  <Sparkline data={tpsPerSlot} color={theme.colors.accent} height={56} />
                </SparklineCard>
                <SparklineCard>
                  <SparklineHeader>
                    <SparklineLabel>
                      {manaStatus && <StatusDot status={manaStatus} />}
                      Mana / block
                    </SparklineLabel>
                    <SparklineValue>
                      {windowMana != null ? formatMana(windowMana) : "—"}
                      {displayHealth.avgManaUtilizationPct != null && (
                        <SparklineAside> ({displayHealth.avgManaUtilizationPct}%)</SparklineAside>
                      )}
                    </SparklineValue>
                  </SparklineHeader>
                  <Sparkline
                    data={manaPerBlock}
                    color={theme.colors.success}
                    height={56}
                  />
                </SparklineCard>
              </SparklineRow>

              {/* Tier 3 — the longest horizon on the card. A column strip, not a line, so it
                  can't be read as continuous with the per-slot sparklines above: 24 discrete
                  epochs against 64 rolling slots. Same segmented language as the timeline, one
                  zoom level out. */}
              <Divider />
              <RecentRow>
                <RecentHeader>
                  <TierLabel style={{ margin: 0 }}>
                    Last {RECENT_EPOCHS} epochs
                    {recentFromEpoch != null && viewEpoch != null && (
                      <TierAside>
                        {" "}
                        #{recentFromEpoch}–#{viewEpoch}
                      </TierAside>
                    )}
                  </TierLabel>
                  {recentSummary && (
                    <RecentValue tone={recentTone?.tone ?? "ok"}>
                      {recentSummary.totals.missedPct.toFixed(2)}% missed
                      <RecentLabel> ({recentSummary.totals.missed})</RecentLabel>
                    </RecentValue>
                  )}
                </RecentHeader>

                {recentSummary?.trend && recentFromEpoch != null && viewEpoch != null && (
                  <EpochMissStrip
                    fromEpoch={recentFromEpoch}
                    toEpoch={viewEpoch}
                    points={recentSummary.trend.points}
                    slotsPerEpoch={slotsPerEpoch}
                    network={selectedNetwork}
                  />
                )}

                <RecentFooter>
                  {recentCauses.length > 0 ? (
                    <CauseDots>
                      {recentCauses.map((c) => (
                        <CauseDotItem key={c.status} title={`${c.status}: ${c.count}`}>
                          <Dot color={slotStatusColor(c.status)} />
                          {c.status} {c.count}
                        </CauseDotItem>
                      ))}
                    </CauseDots>
                  ) : (
                    <RecentLabel>No missed slots in this window</RecentLabel>
                  )}
                  {worstEpoch && (
                    <WorstLink to={`/${selectedNetwork}/health?epoch=${worstEpoch.from}`}>
                      worst epoch {worstEpoch.from} — {worstEpoch.missedPct.toFixed(1)}% &rsaquo;
                    </WorstLink>
                  )}
                </RecentFooter>
              </RecentRow>
            </>
          )}
        </HealthCard>

        <SideColumn>
          <StatsRow>
            <MiniStat>
              <StatValue style={{ fontSize: theme.fontSize.lg }}>
                {Number(stats?.blockCount ?? 0).toLocaleString()}
              </StatValue>
              <StatLabel>Blocks</StatLabel>
            </MiniStat>
            <MiniStat>
              <StatValue style={{ fontSize: theme.fontSize.lg }}>
                {Number(stats?.txCount ?? 0).toLocaleString()}
              </StatValue>
              <StatLabel>Transactions</StatLabel>
            </MiniStat>
            <MiniStat>
              <StatValue style={{ fontSize: theme.fontSize.lg }}>
                {detail?.run?.numClusters ?? "—"}
              </StatValue>
              <StatLabel>Clusters</StatLabel>
            </MiniStat>
          </StatsRow>

          {pieData.length > 0 && (
            <PieCard>
              <PieChartBox>
                <ResponsiveContainer
                  style={{ display: "flex", justifyItems: "center" }}
                  width="100%"
                  height="100%"
                  minWidth={0}
                >
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius="90%"
                      innerRadius="45%"
                      {...noAnimation}
                    >
                      {pieData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={theme.colors.cluster[i % theme.colors.cluster.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </PieChartBox>
              <Legend>
                <StatLabel style={{ marginBottom: theme.spacing.xs }}>Fee Payers</StatLabel>
                <LegendViewport>
                  <LegendRows>
                    {pieData.map((d, i) => (
                      <LegendRow key={i}>
                        <LegendDot color={theme.colors.cluster[i % theme.colors.cluster.length]} />
                        <LegendName>{d.name}</LegendName>
                        <LegendCount>{d.value}</LegendCount>
                      </LegendRow>
                    ))}
                  </LegendRows>
                </LegendViewport>
              </Legend>
            </PieCard>
          )}
        </SideColumn>
      </TopRow>

      {(umapLoading || (umapData && (umapData.clusters.length > 0 || umapData.outliers.length > 0))) && (
        <PlotSection $expanded={plotExpanded}>
          <PlotHeader>
            <PlotCaption>
              3D cluster visualization. Drag to rotate, scroll to zoom, click a point to view.
            </PlotCaption>
            <ExpandButton
              onClick={() => setPlotExpanded((v) => !v)}
              title={
                plotExpanded
                  ? "Back to the dashboard (Esc)"
                  : "Fill the screen with the cluster map"
              }
            >
              {plotExpanded ? <CollapseIcon /> : <ExpandIcon />}
              {plotExpanded ? "Collapse" : "Expand"}
            </ExpandButton>
          </PlotHeader>
          <PlotCard>
            {umapLoading ? (
              <Loading />
            ) : (
              <ScatterPlot3D
                clusters={umapData!.clusters}
                outliers={umapData!.outliers}
                height="100%"
                mobileHeight={plotExpanded ? "100%" : 400}
                onClusterClick={(clusterId) =>
                  navigate(`/${selectedNetwork}/privacy-sets?cluster=${clusterId}`)
                }
                onOutlierClick={(o) => navigate(`/${selectedNetwork}/tx/${o.txHash}`)}
              />
            )}
          </PlotCard>
        </PlotSection>
      )}
    </DashboardContainer>
  );
}
